import React from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import Typography from '@material-ui/core/Typography';
import { Tooltip } from '@material-ui/core';
import LockIcon from '@mui/icons-material/Lock';

import pushToGTM, { selectCategoryTagGA4 } from '../../utils/analytics/gtm';
import { CustomBadge, useStyles } from './styles';
import Button from '../Button';
import SPICE_ICON from '../../images/icons/Compute_SPICE.svg';
import CHART_ICON from '../../images/icons/Computer_Graph.svg';
import WD_ICON from '../../images/icons/Compute_WD+.svg';
import EPS_ICON from '../../images/icons/Compute_EP_Studio.svg';

const CategoryCard = ({
	categoryId,
	pathParam,
	abbreviation,
	title,
	description,
	image,
	released,
	wdSupported,
	chartSupported,
	spiceLiveModelSupported,
	elitePairingStudioSupported,
	redirectParam,
	...rest
}) => {
	const { t } = useTranslation();
	const classes = useStyles({ released });
	const history = useHistory();

	const handleClick = path => {
		if (released) {
			pushToGTM(selectCategoryTagGA4(path));
			history.push(path);
		}
		if (redirectParam && !pathParam) {
			window.open().location.replace(redirectParam);
		}
	};

	return (
		<Card elevation={1} {...rest} className={classes.card}>
			{!released && (
				<CustomBadge sx={{ backgroundColor: '#0076A8' }}>
					<Typography style={{ fontSize: 11, fontWeight: 700, color: '#FFFFFF', marginTop: -2 }}>
						{t('components.categoryCard.text.comingSoon')}
					</Typography>
				</CustomBadge>
			)}
			<CardMedia className={classes.cardMedia}>
				<img src={image} width="100%" height="150" alt={abbreviation} />
			</CardMedia>
			<CardContent className={classes.cardBody}>
				<Typography
					variant={abbreviation ? 'h5' : 'subtitle1'}
					color="#212529"
					onClick={() => handleClick(pathParam)}
					onKeyDown={event => {
						if (event.key === 'Enter' || event.key === ' ') {
							handleClick(pathParam);
						}
					}}
					tabIndex={0}
					role="button"
					className={abbreviation ? classes.pointer : ''}>
					{abbreviation ? abbreviation : '\u00a0'}
				</Typography>

				<Typography variant="body1" color="#212529" className={classes.description}>
					{description}
				</Typography>
			</CardContent>
			<CardContent className={classes.cardFooter}>
				<CardActions className={classes.cardActions}>
					<div className={classes.icons}>
						{spiceLiveModelSupported && (
							<Tooltip title={t('components.categoryCard.text.spiceLiveModelSupported')}>
								<img src={SPICE_ICON} alt="Spice Live Model" style={{ height: '2rem' }} />
							</Tooltip>
						)}
						{elitePairingStudioSupported && (
							<Tooltip title={t('components.categoryCard.text.elitePairingStudioSupported')}>
								<img src={EPS_ICON} alt="EP Studio" style={{ height: '2rem' }} />
							</Tooltip>
						)}
						{chartSupported && (
							<Tooltip title={t('components.categoryCard.text.chartSupported')}>
								<img src={CHART_ICON} alt="Chart" style={{ height: '2rem' }} />
							</Tooltip>
						)}
						{wdSupported && (
							<Tooltip title={t('components.categoryCard.text.webDesigner')}>
								<img src={WD_ICON} alt="WebDesigner+" style={{ height: '2rem' }} />
							</Tooltip>
						)}
					</div>
					<Button
						color="primary"
						variant="contained"
						noTextTransform
						disabled={!released}
						startIcon={categoryId === 'imageSensors' && <LockIcon sx={{ width: '18px' }} />}
						onClick={() => handleClick(pathParam)}
						style={{ fontSize: 16 }}>
						{t('components.categoryCard.buttons.findProducts')}
					</Button>
				</CardActions>
			</CardContent>
		</Card>
	);
};

CategoryCard.propTypes = {
	categoryId: PropTypes.string.isRequired,
	pathParam: PropTypes.string,
	abbreviation: PropTypes.string,
	title: PropTypes.string.isRequired,
	description: PropTypes.string.isRequired,
	image: PropTypes.string.isRequired,
	released: PropTypes.bool.isRequired,
	wdSupported: PropTypes.bool.isRequired
};

export default CategoryCard;
